/*
 * $Id:: StepOne.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import { isEmail } from '../../../../common/utils/service';
import ErrorMessages from '../../../../components/ErrorMessages';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    registrationWrapper: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    gridItem: {
        flex: '1 1 auto',
        marginBottom: '1.5rem',
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    inputItem: {
        marginBottom: '10px',
        flex: '1 1 auto',
    },
    inputError: {
        marginTop: '5px',
    },
    fieldLabel: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: '#546E7A',
    },
    nextButton: {
        minWidth: '65px',
        margin: '0',
        height: '2.5rem',
    },
    infoMessage: {
        maxWidth: '610px',
        marginTop: '5px',
        marginBottom: '15px',
    },
    messageUndelined: {
        textDecoration: 'underline',
    },
}));

const StepFour = ({ gotoNextStep, gotoPreviousStep, error, stepData = {} }) => {
    const classes = useStyles();

    const [username, setUsername] = useState({ value: '', isValid: false, isHidden: true });
    const [email, setEmail] = useState({ value: '', isValid: false });
    const [password, setPassword] = useState({ value: '', showPassword: false });
    const [confirmPassword, setConfirmPassword] = useState({ value: '', showPassword: false });
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    useEffect(() => {
        if (stepData.username) {
            setUsername({ value: stepData.username, isValid: isEmail(stepData.username), isHidden: true });
        } else if (username.isHidden) {
            setUsername({ value: stepData.email, isValid: isEmail(stepData.email), isHidden: true });
        }
        if (stepData.email) setEmail({ value: stepData.email, isValid: isEmail(stepData.email) });
        if (stepData.password) {
            setPassword({ value: stepData.password, showPassword: false, isBlured: true });
            setConfirmPassword({ value: stepData.password, showPassword: false, isBlured: true });
            setPasswordsMatch(true);
        }
    }, [stepData]);

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.password || value.length <= inputsMaxChars.password) {
            setPassword({ ...password, value, isDirty: true });
            setPasswordsMatch(event.target.value === confirmPassword.value);
        }
    };

    const handleUsernameChange = (event) => {
        if (!inputsMaxChars.username || event.target.value.length <= inputsMaxChars.username) {
            setUsername({ ...username, value: event.target.value, isDirty: true });
        }
    };

    const handleEmailChange = (event) => {
        if (!inputsMaxChars.email || event.target.value.length <= inputsMaxChars.email) {
            setEmail({ ...email, value: event.target.value, isDirty: true, isValid: isEmail(event.target.value) });
        }
        if (username.isHidden && (!inputsMaxChars.username || event.target.value.length <= inputsMaxChars.username)) {
            setUsername({ ...username, value: event.target.value });
        }
    };

    const handleConfirmPasswordChange = (event) => {
        if (!inputsMaxChars.password || event.target.value.length <= inputsMaxChars.password) {
            setConfirmPassword({ ...password, value: event.target.value, isDirty: true });
            setPasswordsMatch(event.target.value === password.value);
        }
    };

    const handleOnBlurPassword = () => {
        if (!password.isBlured) setPassword({ ...password, isBlured: true });
    };

    const handleOnBlurConfirmPassword = () => {
        if (!confirmPassword.isBlured) setConfirmPassword({ ...confirmPassword, isBlured: true });
    };

    const toggleVisibility = () => setPassword({ ...password, showPassword: !password.showPassword });

    const toggleConfirmVisibility = () => setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword });

    const submitStep = (event) => {
        event.preventDefault();
        const isFormValid = email.value && password.value && email.isValid && passwordsMatch;
        if (!isFormValid) {
            setPassword({ ...password, isDirty: true, isBlured: true });
            setConfirmPassword({ ...confirmPassword, isDirty: true, isBlured: true });
            setEmail({ ...email, isDirty: true });
            return;
        }
        const formData = { email: email.value, password: password.value, userName: username.value };
        gotoNextStep(formData);
    };

    const passwordsDoNotMatch = !!password.value && password.isBlured && confirmPassword.isBlured && password.value !== confirmPassword.value;
    const passwordError = (error && error.description) || '';
    const emailError = (error && error.description === 'reused.username' && error.description) || '';

    return (
        <div className={classes.registrationWrapper}>
            <form onSubmit={submitStep} noValidate>
                <Grid container direction={'column'} className={classes.formWrapper}>
                    {!username.isHidden && (
                        <Grid item xs md sm className={classes.gridItem}>
                            <InputLabel
                                error={!!emailError || (username.isDirty && !username.value)}
                                htmlFor={'username'}
                                className={classes.fieldLabel}>
                                {t('your.username')}*
                            </InputLabel>
                            <Input
                                id={'username'}
                                className={classes.textInput}
                                placeholder={t('type.your.username')}
                                type={'text'}
                                name={'username'}
                                error={!!emailError || (username.isDirty && !username.value)}
                                fullWidth
                                autoFocus
                                required
                                onChange={handleUsernameChange}
                                value={username.value}
                            />
                            {username.isDirty && !username.value && (
                                <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                    {t('required.field')}
                                </Typography>
                            )}
                        </Grid>
                    )}
                    <Grid item xs md sm className={classes.gridItem}>
                        <InputLabel error={!!emailError || (email.isDirty && !email.value)} htmlFor={'email'} className={classes.fieldLabel}>
                            {t('your.email')}*
                        </InputLabel>
                        <Input
                            id={'email'}
                            className={classes.textInput}
                            placeholder={t('type.your.email')}
                            type={'text'}
                            name={'email'}
                            error={!!emailError || (email.isDirty && !email.value)}
                            fullWidth
                            autoFocus
                            required
                            onChange={handleEmailChange}
                            value={email.value}
                        />
                        {email.isDirty && !email.isValid && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t(!!email.value ? 'invalid.email.format' : 'required.field')}
                            </Typography>
                        )}
                        {!!emailError && (
                            <Typography color={'error'} className={classes.infoMessage}>
                                {t(emailError)}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs sm md className={classes.gridItem}>
                        <InputLabel
                            htmlFor={'password'}
                            error={!!passwordError || (password.isDirty && !password.value) || passwordsDoNotMatch}
                            className={classes.fieldLabel}>
                            {t('password')}*
                        </InputLabel>
                        <Input
                            id={'password'}
                            className={classes.textInput}
                            placeholder={t('type.password')}
                            type={password.showPassword ? 'text' : 'password'}
                            name={'password'}
                            fullWidth
                            onChange={handlePasswordChange}
                            onBlur={handleOnBlurPassword}
                            value={password.value}
                            endAdornment={
                                <IconButton onClick={toggleVisibility} type={'button'} disableRipple>
                                    {password.showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                                </IconButton>
                            }
                            error={!!passwordError || (password.isDirty && !password.value) || passwordsDoNotMatch}
                        />
                        {password.isDirty && !password.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                        {passwordsDoNotMatch && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('entering.same.value')}
                            </Typography>
                        )}
                        <Typography variant={'body2'} className={`${classes.infoMessage} ${classes.messageUndelined}`}>
                            {t('password.pattern.description')}
                        </Typography>
                    </Grid>
                    <Grid item xs sm md className={classes.gridItem}>
                        <InputLabel
                            htmlFor={'confirmPassword'}
                            error={!!passwordError || (confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                            className={classes.fieldLabel}>
                            {t('repeat.password')}*
                        </InputLabel>
                        <Input
                            id={'confirmPassword'}
                            className={classes.textInput}
                            placeholder={t('type.password')}
                            type={confirmPassword.showPassword ? 'text' : 'password'}
                            name={'confirmPassword'}
                            fullWidth
                            onChange={handleConfirmPasswordChange}
                            onBlur={handleOnBlurConfirmPassword}
                            value={confirmPassword.value}
                            endAdornment={
                                <IconButton onClick={toggleConfirmVisibility} type={'button'} disableRipple>
                                    {confirmPassword.showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                                </IconButton>
                            }
                            error={!!passwordError || (confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                        />
                        {confirmPassword.isDirty && !confirmPassword.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                        <Typography variant={'body2'} className={classes.infoMessage}>
                            {t('make.sure.matches')}
                        </Typography>
                        {!!passwordError && (
                            <Grid item xs sm md>
                                <ErrorMessages errorMessage={passwordError} classes={`${classes.inputError}`} />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid container spacing={0} className={classes.buttonGroup}>
                    <Grid item>
                        <Button variant="outlined" type="button" className={classes.editButton} onClick={gotoPreviousStep}>
                            {t('back')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" className={classes.submitButton} onClick={submitStep} >
                            {t('create.user')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );
};

export default StepFour;
